// LIH Assessment — Unified Question Set v2.0
// 39 questions across 7 sections
// NIH v2 + LIH lifestyle additions merged
// New: q6b_fruit, q_health_rating, q4_entry_priority, q5_diet_quality,
//      q17_energy, q23_current_supps, q24_shaklee_exp, q28_commitment
//      q_sleep_hrs, q_sleep_issues, q_strength, q_sport, q_mindbody,
//      q_nature, q_civic, q_upf, q_window, q_bloodwork

export const sections = [

  {
    id: 'about_you',
    title: 'About You',
    subtitle: 'A little context so everything that follows is specific to you.',
    questions: [
      {
        id: 'q1_age', text: 'What is your age range?', type: 'single', required: true,
        options: ['Under 40', '40–54', '50–64', '65–74', '75 or older'],
      },
      {
        id: 'q2_sex', text: 'Which best describes you?', type: 'single', required: true,
        helper: 'Used to calibrate multivitamin selection and hormonal context.',
        options: ['Female', 'Male', 'Non-binary', 'Prefer not to say'],
      },
      {
        id: 'q3_life_stage', text: 'Which best describes your current life stage?', type: 'single', required: true,
        options: ['Not applicable', 'Pregnant or planning pregnancy', 'Postpartum / breastfeeding', 'Perimenopausal (transitioning)', 'Postmenopausal'],
      },
      {
        id: 'q_health_rating', text: 'How would you describe your overall health right now?', type: 'single', required: true,
        options: ['Not where I want to be', 'Fair — room for real improvement', 'Good — I want to optimize', 'Excellent — I want to maintain and extend'],
      },
    ],
  },

  {
    id: 'how_you_feel',
    title: 'How You Feel',
    subtitle: 'Your energy, sleep, and stress — the foundation of everything.',
    questions: [
      {
        id: 'q17_energy', text: 'How would you describe your energy levels through the day?', type: 'single', required: true,
        options: ['Consistently good — steady energy throughout', 'Variable — good in the morning, drops in the afternoon', 'Often low — I frequently feel fatigued', 'Very low — fatigue is a significant and ongoing issue'],
      },
      {
        id: 'q_sleep_hrs', text: 'How many hours do you typically sleep each night?', type: 'single', required: true,
        options: ['Under 5 hours', '5–6 hours', '6–7 hours', '7–8 hours', '8–9 hours', 'Over 9 hours'],
      },
      {
        id: 'q15_sleep', text: 'How would you rate your sleep quality overall?', type: 'single', required: true,
        options: ['Excellent — I fall asleep easily and wake refreshed', 'Good — generally restful with occasional disruptions', 'Fair — I often have trouble falling or staying asleep', 'Poor — sleep is a significant ongoing challenge'],
      },
      {
        id: 'q_sleep_issues', text: 'Do any of these sound familiar?', type: 'multi', required: false,
        helper: 'Select all that apply.',
        options: ['Difficulty falling asleep', 'Waking up frequently during the night', 'Waking too early and unable to get back to sleep', 'Significant daytime fatigue or sleepiness', 'Snoring, or a partner has noticed you stop breathing during sleep', 'None of these'],
        allowOther: true,
      },
      {
        id: 'q16_stress', text: 'What is your typical stress level?', type: 'single', required: true,
        options: ['Low — I manage stress well', 'Moderate — typical life pressures I generally handle', 'High — ongoing significant stress that affects my wellbeing', 'Very high — stress is significantly impacting my daily life'],
      },
    ],
  },

  {
    id: 'how_you_live',
    title: 'How You Live',
    subtitle: 'Movement, habits, and the daily patterns that shape your health.',
    questions: [
      {
        id: 'q13_exercise', text: 'How would you describe your overall fitness level?', type: 'single', required: true,
        options: ['Very active — structured exercise 5+ days per week', 'Active — regular exercise 3–4 days per week', 'Moderately active — light exercise or walking most days', 'Lightly active — occasional movement', 'Sedentary — little to no regular exercise'],
      },
      {
        id: 'q14_exercise_type', text: 'What types of activity do you do most?', type: 'multi', required: false,
        helper: 'Select all that apply.',
        options: ['Strength training / resistance exercise', 'Endurance sports (running, cycling, swimming)', 'Team or competitive sports', 'Yoga, Pilates, or flexibility work', 'Walking or light activity', "I'm not currently active"],
        allowOther: true,
      },
      {
        id: 'q_strength', text: 'Do you do any resistance or strength training?', type: 'single', required: true,
        options: ['Not currently', 'Once a week', 'Twice a week', 'Three or more times a week'],
      },
      {
        id: 'q_sport', text: 'Do you play any sport or physical activity that involves skill, social connection, or some element of competition?', type: 'single', required: false,
        helper: 'Tennis, golf, pickleball, martial arts, climbing, rowing, team sports — anything with skill development and social dimension.',
        options: ['Yes, regularly', 'Yes, occasionally', "Not currently, but I'd like to", "No, and it's not something I'm interested in"],
      },
      {
        id: 'q_mindbody', text: 'Do you currently practice any of the following?', type: 'multi', required: true,
        helper: 'Select all that apply.',
        options: ['Meditation (any form — TM, mindfulness, contemplative)', 'Yoga', 'Tai chi or qigong', 'Breathwork (Wim Hof, box breathing, pranayama, or similar)', 'Regular prayer or religious / spiritual community practice', 'Journaling or reflective writing practice', 'None currently'],
        allowOther: true,
      },
      {
        id: 'q_nature', text: 'Do you have regular hands-on contact with nature — gardening, growing things, habitat work, or time outdoors?', type: 'single', required: false,
        options: ["Yes — it's a regular part of my life", 'Sometimes, but not consistently', 'Rarely or not at all'],
      },
      {
        id: 'q_arts', text: 'Do you have an active creative practice — music, visual art, writing, crafts, or any skill-based creative work?', type: 'single', required: false,
        helper: 'Skill-based creative practice has documented effects on neuroplasticity and cognitive aging.',
        options: ["Yes — it's a regular part of my life", "I used to, but I've let it go", "No, but I'm curious about this", "No, and it's not something I'm drawn to"],
      },
      {
        id: 'q_civic', text: 'Are you part of any organized community, civic, volunteer, or purpose-driven activities on a regular basis?', type: 'single', required: false,
        options: ['Yes, with structured and ongoing involvement', 'Now and then', "No, but I'm thinking about it", "No, and it's not a focus for me"],
      },
      {
        id: 'q11_sun', text: 'How much direct sun exposure do you get on most days?', type: 'single', required: true,
        options: ['Regular outdoor time — 20+ minutes of direct sun most days', 'Some — occasional outdoor time', 'Minimal — mostly indoors or always use sunscreen', 'Very little — climate or lifestyle prevents regular sun exposure'],
      },
      {
        id: 'q_upf', text: 'How often do ultra-processed foods show up in your diet?', type: 'single', required: true,
        helper: 'Ultra-processed: packaged snacks, fast food, ready meals, sugary drinks, most breakfast cereals.',
        options: ['Rarely or never', 'A few times a week', 'Most days', "I honestly don't know"],
      },
      {
        id: 'q_window', text: 'What does your typical eating window look like?', type: 'single', required: false,
        helper: 'The hours between your first and last food or caloric drink of the day.',
        options: ['Under 10 hours', '10–12 hours', '12–14 hours', 'Over 14 hours', 'No consistent pattern'],
      },
    ],
  },

  {
    id: 'priorities',
    title: 'What Matters to You',
    subtitle: 'Your goals and priorities — what you most want to change or protect.',
    questions: [
      {
        id: 'q19_top_priorities', text: 'Which health goals are most important to you right now?', type: 'multi', required: true,
        helper: 'Select up to 5 that matter most.',
        maxSelect: 5,
        options: [
          'Maintaining or building energy and vitality',
          'Supporting healthy aging and longevity',
          'Optimizing cognitive function and mental sharpness',
          'Supporting cardiovascular and heart health',
          'Building or maintaining bone density',
          'Weight management and healthy body composition',
          'Managing stress and supporting emotional wellbeing',
          'Improving sleep quality and restoration',
          'Strengthening immune resilience',
          'Digestive health and gut function',
          'Joint health and mobility',
          'Athletic performance and recovery',
          'Skin, hair, and collagen health',
        ],
        allowOther: true,
      },
      {
        id: 'q20_hormonal', text: 'Are you navigating any of these hormonal or life-stage transitions?', type: 'multi', required: false,
        helper: 'Select all that apply.',
        options: ['Perimenopause — experiencing symptoms like hot flashes, mood shifts, or sleep changes', 'Post-menopause — supporting health in the years after menopause', "Men's urinary or prostate wellness — noticing changes in urinary flow or frequency", 'None of the above'],
        allowOther: true,
      },
      {
        id: 'q21_metabolic', text: 'Do any of these metabolic wellness goals resonate with you?', type: 'multi', required: false,
        helper: 'Select all that apply.',
        options: ["I'd like to support healthy blood sugar levels already within the normal range", "I'd like to support healthy cholesterol levels already within the normal range", "I'd like to support healthy blood pressure levels already within the normal range", 'None of the above'],
      },
      {
        id: 'q22_specific', text: "Any other specific wellness areas you'd like to address?", type: 'multi', required: true,
        helper: 'Select all that apply.',
        options: ['Eye health and visual longevity', 'Liver and detoxification support', 'Skin elasticity and visible aging', 'Hair and nail strength', 'Recovery from physical exertion', 'Mental focus and clarity under pressure', 'Mood balance and emotional resilience', 'None of the above'],
        allowOther: true,
      },
      {
        id: 'q18_blood_thinners', text: 'Are you currently taking blood-thinning medications (warfarin, Eliquis, Xarelto, or similar)?', type: 'single', required: true,
        helper: 'This affects multivitamin selection and omega-3 guidance.',
        options: ['Yes', 'No', 'Not sure'],
      },
    ],
  },

  {
    id: 'diet',
    title: 'Your Diet',
    subtitle: "Not a judgment — just the context the recommendations need.",
    questions: [
      {
        id: 'q5_diet_quality', text: 'How would you describe your overall diet quality?', type: 'single', required: true,
        options: ['Excellent — mostly whole foods, vegetables, lean proteins, minimal processed foods', 'Good — generally healthy with some processed foods or gaps', 'Mixed — some healthy choices but significant room for improvement', 'Poor — mostly processed or convenience foods, few vegetables'],
      },
      {
        id: 'q6_vegetables', text: 'How many servings of vegetables do you typically eat daily?', type: 'single', required: true,
        helper: 'One serving = ½ cup cooked or 1 cup raw.',
        options: ['5 or more', '3–4', '1–2', 'Less than 1'],
      },
      {
        id: 'q6b_fruit', text: 'How many servings of fruit do you typically eat daily?', type: 'single', required: true,
        helper: 'One serving = a medium piece of fruit, or ½ cup.',
        options: ['3 or more', '1–2', 'Less than 1', 'Rarely or never'],
      },
      {
        id: 'q7_protein', text: 'How do you typically meet your protein needs?', type: 'multi', required: true,
        helper: 'Select all that apply.',
        options: ['Animal proteins (meat, poultry, fish)', 'Dairy and eggs', 'Plant proteins (beans, legumes, tofu)', 'Protein supplements or shakes', "I'm not sure I'm getting enough protein"],
        allowOther: true,
      },
      {
        id: 'q8_fish', text: 'How often do you eat fatty fish (salmon, sardines, mackerel, herring)?', type: 'single', required: true,
        options: ['3+ times per week', '1–2 times per week', 'A few times per month', 'Rarely or never'],
      },
      {
        id: 'q9_dairy', text: 'Do you consume dairy products regularly?', type: 'single', required: true,
        options: ['Yes, daily', 'Occasionally', 'No — lactose intolerant or digestive sensitivity', 'No — personal preference or plant-based diet'],
      },
      {
        id: 'q10_diet_type', text: 'Do you follow any of these dietary patterns?', type: 'multi', required: false,
        helper: 'Select all that apply.',
        options: ['Plant-based or vegan', 'Vegetarian', 'Mediterranean or whole foods', 'Low-carb or ketogenic', 'Gluten-free', 'No specific pattern'],
        allowOther: true,
      },
      {
        id: 'q12_sugar', text: 'How would you describe your consumption of sugar and highly processed foods?', type: 'single', required: true,
        options: ['Very low — I actively limit these', 'Moderate — occasional treats', 'Frequent — these are a regular part of my diet', 'High — I know this is an area I need to work on'],
      },
      {
        id: 'q_bloodwork', text: 'Have you had relevant bloodwork done in the past year?', type: 'single', required: false,
        helper: 'Lipid panel, vitamin D, HbA1c, or similar.',
        options: ['Yes, and I know my numbers', "Yes, but I don't remember the specifics", 'No', "I'm not sure"],
      },
      {
        id: 'q4_entry_priority', text: 'How do you want to approach your health improvements?', type: 'single', required: true,
        helper: "There's no wrong answer — this shapes how Lumen coaches you.",
        options: ['Starting with supplements — I can add something new more easily than changing habits right now', 'Starting with diet and lifestyle — I want to build the foundation first', "Both together — I'm ready to work on diet and supplements simultaneously", "Not sure yet — I'd like guidance on where to start"],
      },
    ],
  },

  {
    id: 'current',
    title: 'Your Supplement Context',
    subtitle: "What you're already doing — so we don't duplicate it.",
    questions: [
      {
        id: 'q23_current_supps', text: "Which supplements are you currently taking?", type: 'multi', required: false,
        helper: 'Select all that apply.',
        options: ['A multivitamin or multimineral', 'Omega-3 / fish oil', 'Vitamin D', 'Magnesium', 'Protein supplement or meal replacement', 'Probiotic', 'Vitamin C', 'B vitamins or B-complex', 'Calcium', 'CoQ10', 'Other supplements', "I'm not currently taking any supplements"],
        allowOther: true,
        otherTrigger: 'Other supplements',
      },
      {
        id: 'q24_shaklee_exp',
        text: 'When it comes to supplements, which best describes you?',
        type: 'single',
        required: false,
        options: [
        // eslint-disable-next-line no-useless-escape
          "I\'m new to this — I want clear guidance and to understand the reasons behind it",
          "I take some basics — I\'m looking to optimize what I\'m already doing",
          "I\'ve researched this extensively — I want the evidence, not the overview",
          "I\'m honestly skeptical — I need to see the science before I commit to anything",
        ],
      },

    ],
  },
  {
    id: 'preferences',
    title: 'Your Preferences',
    subtitle: 'So the protocol works for your actual life.',
    questions: [
      {
        id: 'q26_budget', text: 'How would you describe your monthly supplement investment level?', type: 'single', required: true,
        helper: 'Minimal budgets get the highest-impact essentials first.',
        options: ['Minimal — I can invest in 1–2 core products ($30–60/month)', 'Moderate — I can build a focused protocol ($60–150/month)', "Generous — I'm ready for a comprehensive approach ($150–300/month)", 'Comprehensive — I want the full picture regardless of cost'],
      },
      {
        id: 'q27_format', text: 'Do you have any supplement format preferences or limitations?', type: 'multi', required: false,
        helper: 'Select all that apply.',
        options: ['I prefer capsules or tablets', 'I prefer powders or drink mixes', 'I prefer gummies', 'I have difficulty swallowing tablets or capsules', 'I prefer soy-based protein products', 'I avoid soy', 'No strong preference'],
        allowOther: true,
      },
    ],
  },
];

export const allQuestions = sections.flatMap(s => s.questions);
